import {
  type RowActionsProps,
  type StyleConfigProps,
  type VisibilityElementProps,
} from '@blaze-runtime/engine-domain';
import {
  type CalendarDateSelectorState,
  type CalendarDateSelectorStyle,
  type CanvasElementPropertiesWithData,
  type ElementLabelProps,
  type ElementTitleStyleProps,
  type PropertiesConfigInputColorModel,
  type PropertiesConfigInputColorPaletteModel,
  type PropertiesConfigInputContainerItemActionModel,
  type PropertiesConfigInputSpacingAndAlignmentModel,
  type PropertiesConfigInputVisualEffectModel,
} from '@blaze-shared/elements-domain';
import { type ValueWithUnitOfMeasure } from '@blaze-shared/shared-util';

import { CalendarDay } from '../../components/calendar-dates-view';

import {
  CalendarDateFormat,
  CalendarTimeFormat,
  CalendarTimeScale,
  CalendarView,
} from './calendar.models';
import { calendarConfig } from './providers/config';

type PropsModel = CanvasElementPropertiesWithData &
  StyleConfigProps &
  VisibilityElementProps &
  RowActionsProps;

export type CalendarDayViewColumnWidthMode = 'fit' | 'count' | 'fixed';
export interface CalendarDayViewOptions {
  widthMode: CalendarDayViewColumnWidthMode;
  widthValue: ValueWithUnitOfMeasure;
  showCalendarsNumber: number;
}

export type CalendarPropsModel = PropsModel & {
  version?: number;
  content: {
    title: ElementLabelProps;
    calendarsLabel: string;
    time?: {
      from?: string;
      to?: string;

      // backwards compatibility
      min?: string;
      max?: string;
      enabled?: boolean;
    };
    input: {
      titleColumn?: string;
      startColumn?: string;
      endColumn?: string;
      calendarColumn?: string;
      timeZoneColumn?: string;
    };
    output: {
      actions: PropertiesConfigInputContainerItemActionModel[];
    };
    calendarOptions: {
      view: CalendarView;
      startWeekOn: CalendarDay;
      timeFormat: CalendarTimeFormat;
      timeScale: CalendarTimeScale;
      dateFormat: CalendarDateFormat;
      defaultDurationMinutes: number;
      hideWeekends: boolean;
      hideCalendarName: boolean;
      disableEventDrag?: boolean;
    };
    dayViewOptions: CalendarDayViewOptions;
  };
  style: {
    general: {
      offset: PropertiesConfigInputSpacingAndAlignmentModel;
      visualEffect: PropertiesConfigInputVisualEffectModel;
      titles: PropertiesConfigInputColorModel;
      gridBackground: PropertiesConfigInputColorModel;
      headerBackground: PropertiesConfigInputColorModel;
      lines: PropertiesConfigInputColorModel;
      secondaryLines: PropertiesConfigInputColorModel;
    };
    titleBlock: ElementTitleStyleProps;
    dateSelector: {
      currentState: CalendarDateSelectorState;
      default: CalendarDateSelectorStyle;
      hover: CalendarDateSelectorStyle;
      selected: CalendarDateSelectorStyle;
      dayWithEvents: PropertiesConfigInputColorModel;
    };
    appointments: {
      palette: PropertiesConfigInputColorPaletteModel;
      outline: PropertiesConfigInputColorModel;
      fontSize: ValueWithUnitOfMeasure;
    };
    container: {
      outline: PropertiesConfigInputColorModel;
      outlineWidth: ValueWithUnitOfMeasure;
    };
  };
};

export const CALENDAR_DEFAULTS: CalendarPropsModel = {
  version: 1,
  content: {
    title: {
      enabled: true,
      label: 'Calendar',
      align: 'left',
      iconColor: { color1: '$t.title', selectorType: 'solid' },
    },
    calendarsLabel: 'Available Calendars',
    time: {
      enabled: false,
    },
    input: {},
    output: {
      actions: [],
    },
    itemAdd: { enabled: false, label: '' },
    itemEdit: { enabled: false },
    itemDelete: { enabled: false },
    itemViewDialog: { enabled: false },
    calendarOptions: {
      view: CalendarView.Day,
      startWeekOn: CalendarDay.Monday,
      timeFormat: CalendarTimeFormat['12H'],
      timeScale: CalendarTimeScale['1H'],
      dateFormat: CalendarDateFormat.FullDate,
      defaultDurationMinutes: 60,
      hideWeekends: false,
      hideCalendarName: true,
      disableEventDrag: false,
    },
    dayViewOptions: {
      widthMode: 'fixed',
      widthValue: `${calendarConfig.defaultDayViewColumnWidthPx}px`,
      showCalendarsNumber: 5,
    },
  },
  style: {
    general: {
      offset: { margin: '$t.spacingExternal', padding: '$t.spacingInternal' },
      visualEffect: {
        type: '$t.effectType',
        data: {
          x: '$t.effectX',
          y: '$t.effectY',
          blur: '$t.effectBlur',
          color1: '$t.effectColor1',
        },
      },
      titles: { color1: '$t.title', selectorType: 'solid' },
      gridBackground: { color1: '$t.fillInternal', selectorType: 'solid' },
      headerBackground: { color1: '$t.complementary', selectorType: 'solid' },
      lines: { color1: '$t.outline', selectorType: 'solid' },
      secondaryLines: { color1: '$t.outline_lighter', selectorType: 'solid' },
    },
    titleBlock: {
      fontStyle: '$t.headerFontStyle' as any,
      fontSize: '$t.headerFontSize',
      fontFamily: '$t.headerFontFamily',
      fontWeight: '$t.headerFontWeight',
      iconSize: '$t.headerIconSize',
    },
    dateSelector: {
      currentState: 'default',
      default: {
        day: { color1: '$t.value', selectorType: 'solid' },
        fill: { color1: '$t.canvas', selectorType: 'solid' },
        outline: { color1: '$t.canvas', selectorType: 'none' },
        outlineWidth: '1px',
        radius: '16px',
      },
      hover: {
        day: { color1: '$t.value', selectorType: 'solid' },
        fill: { color1: '$t.canvas', selectorType: 'solid' },
        outline: { color1: '$t.accent', selectorType: 'solid' },
        outlineWidth: '1px',
        radius: '16px',
      },
      selected: {
        day: { color1: '$t.fill', selectorType: 'solid' },
        fill: { color1: '$t.accent', selectorType: 'solid' },
        outline: { color1: '$t.canvas', selectorType: 'none' },
        outlineWidth: '1px',
        radius: '16px',
      },
      dayWithEvents: { color1: '$t.value_a15', selectorType: 'solid' },
    },
    appointments: {
      palette: {
        paletteName: 'palette2',
        foreground: '$t.palettes.palette2.fg',
        background: '$t.value',
      },
      outline: { color1: '$t.fill', selectorType: 'solid' },
      fontSize: '$t.fontSize',
    },
    container: {
      outlineWidth: '$t.containerOutlineWidth',
      outline: { color1: '$t.outline', selectorType: '$t.containerOutlineType' as any },
    },
  },
};
